<script setup lang="ts">
import { computed, ref } from "vue";
import type { Workspace, WorkspaceOrder } from "@artech-global/types";

const { t } = useI18n();
const { d, c } = useFormat();

type WorkspaceOrderRow = WorkspaceOrder & {
  workspace?: Pick<Workspace, "id" | "name"> | null;
};

function workspaceOrderStatusIntent(status: string) {
  switch (status) {
    case "confirmed":
      return "positive";
    case "pending":
    case "draft":
      return "warning";
    case "failed":
    case "cancelled":
    case "expired":
      return "negative";
    default:
      return "neutral";
  }
}

function workspaceOrderCycleLabel(cycle: string | undefined, t: (key: string) => string) {
  if (!cycle) return "—";
  const key = `workspaceOrder.cycle.${cycle}`;
  const label = t(key);
  return label === key ? cycle : label;
}

const open = ref(false);
const order = ref<WorkspaceOrderRow | null>(null);

const invoiceNo = computed(() => {
  if (order.value?.code) return order.value.code;
  if (!order.value?.id) return "—";
  return `#${order.value.id.slice(-8).toUpperCase()}`;
});

const periodRange = computed(() => {
  const current = order.value;
  if (!current?.periodStartTime || !current?.periodEndTime) return "—";
  return `${d(current.periodStartTime, "long")} – ${d(current.periodEndTime, "long")}`;
});

const isChange = computed(() => order.value?.type === "change");
const isCustom = computed(() => order.value?.type === "custom");

function show(data: WorkspaceOrderRow) {
  order.value = data;
  open.value = true;
}

function close() {
  open.value = false;
  order.value = null;
}

defineExpose({ open: show, close });
</script>

<template>
  <Modal
    :open="open"
    size="md"
    :title="t('workspaceOrder.modal.title', { code: invoiceNo })"
    :closable="true"
    @close="close"
  >
    <div v-if="order" class="flex flex-col gap-large">
      <div class="flex flex-col gap-medium md:flex-row md:items-start md:justify-between">
        <div class="flex flex-col gap-2xsmall">
          <div class="flex items-center gap-small">
            <span class="h4-primary">{{ invoiceNo }}</span>
            <Badge :intent="workspaceOrderStatusIntent(order.status)" emphasis="medium" size="small">
              {{ t(`workspaceOrder.status.${order.status}`) }}
            </Badge>
          </div>
          <span class="text-body-small-tertiary">{{ periodRange }}</span>
        </div>
        <div class="flex flex-col gap-3xsmall items-start md:items-end">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.modal.amount') }}</span>
          <span class="h3-primary">{{ c(order.amount || 0, order.currency || 'USD') }}</span>
        </div>
      </div>

      <Divider emphasis="light" />

      <div class="grid gap-medium grid-cols-2 md:gap-x-large">
        <div class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.columnWorkspace') }}</span>
          <span class="text-body-secondary">{{ order.workspace?.name || '—' }}</span>
        </div>
        <div class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.columnType') }}</span>
          <span class="text-body-secondary">{{ t(`workspaceOrder.type.${order.type}`) }}</span>
        </div>
        <div v-if="isChange" class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.modal.oldPlan') }}</span>
          <span class="text-body-secondary">{{ order.oldPlan?.name || '—' }}</span>
        </div>
        <div v-if="isCustom" class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('finance.sale.contractName') }}</span>
          <span class="text-body-secondary">{{ order.description || '—' }}</span>
        </div>
        <template v-else>
          <div class="flex flex-col gap-3xsmall">
            <span class="text-body-small-tertiary">{{ isChange ? t('workspaceOrder.modal.newPlan') : t('workspaceOrder.columnPlan') }}</span>
            <span class="text-body-secondary">{{ order.plan?.name || '—' }}</span>
          </div>
          <div class="flex flex-col gap-3xsmall">
            <span class="text-body-small-tertiary">{{ t('workspaceOrder.columnCycle') }}</span>
            <span class="text-body-secondary">{{ workspaceOrderCycleLabel(order.billingCycle, t) }}</span>
          </div>
          <div class="flex flex-col gap-3xsmall">
            <span class="text-body-small-tertiary">{{ t('workspaceOrder.modal.period') }}</span>
            <span class="text-body-secondary">{{ periodRange }}</span>
          </div>
        </template>
        <div class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.modal.paymentMethod') }}</span>
          <span class="text-body-secondary">
            <template v-if="order.paymentMethod?.cardLast4">
              {{ order.paymentMethod.cardBrand?.toUpperCase() || 'CARD' }} •••• {{ order.paymentMethod.cardLast4 }}
            </template>
            <template v-else>{{ order.paymentMethodType || '—' }}</template>
          </span>
        </div>
        <div class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.modal.gateway') }}</span>
          <span class="text-body-secondary">{{ order.paymentGateway || '—' }}</span>
        </div>
        <div class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.columnCreateTime') }}</span>
          <span class="text-body-secondary">{{ order.createTime ? d(order.createTime, 'datetime') : '—' }}</span>
        </div>
        <div class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.modal.expireTime') }}</span>
          <span class="text-body-secondary">{{ order.expireTime ? d(order.expireTime, 'datetime') : '—' }}</span>
        </div>
        <div v-if="!isCustom && order.description" class="flex flex-col gap-3xsmall">
          <span class="text-body-small-tertiary">{{ t('field.description') }}</span>
          <span class="text-body-secondary">{{ order.description }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-xsmall padding-medium bg-canvas radius-xsmall">
        <div class="flex items-center justify-between">
          <span class="text-body-small-tertiary">
            {{ order.plan?.name || order.description || t('workspaceOrder.columnPlan') }}
          </span>
          <span class="text-body-small-tertiary">{{ c(order.amount || 0, order.currency || 'USD') }}</span>
        </div>
        <div class="flex items-center justify-between">
          <span class="text-body-small-tertiary">{{ t('workspaceOrder.modal.totalDue') }}</span>
          <span class="text-body-primary">{{ c(order.amount || 0, order.currency || 'USD') }}</span>
        </div>
      </div>
    </div>
  </Modal>
</template>
